import React, { useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useCommonUI } from "../contexts/CommonUIContext";
import LabelChip from "./LabelChip";
import ToggleSwitch from "./ToggleSwitch";
import caretDown from "../assets/icon-arrow-down.svg";
import caretUp from "../assets/icon-arrow-up.svg";
import { LABEL_PRESET_COLORS, DEFAULT_LABEL_COLOR, HEX_RE } from "../constants/labelColors";

// Single management dialog for labels + action templates, mirroring Android's
// LabelPickerDialog / LabelEditDialog / ActionTemplatePicker+Edit dialogs.
// Rendered by CommonUIProvider (above FirebaseProvider), so all data + CRUD
// come in as props. One stateful component with internal views avoids nested
// modals.
const ManageLabelsDialog = ({
  labels = [],
  projects = [],
  projectId = null,
  createLabel,
  updateLabel,
  deleteLabel,
  actionTemplates = [],
  createActionTemplate,
  updateActionTemplate,
  deleteActionTemplate,
}) => {
  const { closeModal } = useCommonUI();
  const [view, setView] = useState("list"); // list | editLabel | color | templates | editTemplate

  // ── label edit form ──
  const [editingLabel, setEditingLabel] = useState(null);
  const [lName, setLName] = useState("");
  const [lColor, setLColor] = useState(DEFAULT_LABEL_COLOR);
  const [lGlobal, setLGlobal] = useState(true);
  const [lTemplateId, setLTemplateId] = useState(null);
  // Which project a non-global label belongs to (editable via dropdown).
  const [lProjectId, setLProjectId] = useState(null);
  const [customHex, setCustomHex] = useState("");
  const [error, setError] = useState("");

  const firstProjectId = projects[0] != null ? Number(projects[0].id) : null;

  // ── template edit form ──
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [tName, setTName] = useState("");
  const [tSteps, setTSteps] = useState([""]);
  const [tGlobal, setTGlobal] = useState(true);
  // Where to return after the template flows, so the same Templates list /
  // editor can be reached from the labels list OR from inside the label editor.
  const [tplListReturn, setTplListReturn] = useState("list");
  const [tplEditReturn, setTplEditReturn] = useState("templates");

  const openLabel = (label) => {
    setEditingLabel(label);
    setLName(label?.name || "");
    setLColor(label?.color || DEFAULT_LABEL_COLOR);
    setLGlobal(label ? label.projectId == null : true);
    setLTemplateId(label?.defaultActionTemplateId ?? null);
    // Seed the project dropdown: the label's own project, else the current
    // scope, else the first project — so flipping "global" off has a target.
    setLProjectId(
      label?.projectId != null
        ? Number(label.projectId)
        : projectId != null
        ? Number(projectId)
        : firstProjectId
    );
    setError("");
    setView("editLabel");
  };

  const saveLabel = async () => {
    const trimmed = lName.trim();
    if (!trimmed) return;
    const scope = lGlobal ? null : lProjectId;
    if (editingLabel) {
      const ok = await updateLabel(editingLabel.id, {
        name: trimmed,
        color: lColor,
        projectId: scope,
        defaultActionTemplateId: lTemplateId,
      });
      if (!ok) return setError("A label with that name already exists in this scope.");
    } else {
      const id = await createLabel(trimmed, lColor, scope);
      if (!id) return setError("A label with that name already exists in this scope.");
      if (lTemplateId != null) await updateLabel(id, { defaultActionTemplateId: lTemplateId });
    }
    setView("list");
  };

  const openTemplate = (tpl, ret = "templates") => {
    setEditingTemplate(tpl);
    setTName(tpl?.name || "");
    setTSteps(tpl?.actionLabels?.length ? [...tpl.actionLabels] : [""]);
    setTGlobal(tpl ? tpl.projectId == null : true);
    setTplEditReturn(ret);
    setView("editTemplate");
  };

  const saveTemplate = async () => {
    const trimmed = tName.trim();
    if (!trimmed) return;
    const cleaned = tSteps.map((s) => s.trim()).filter(Boolean);
    const scope = tGlobal ? null : projectId;
    if (editingTemplate) {
      await updateActionTemplate(editingTemplate.id, { name: trimmed, actionLabels: cleaned, projectId: scope });
    } else {
      const newDocId = await createActionTemplate(trimmed, cleaned, scope);
      // Created from the label editor → pre-select it as the label's default so
      // the user doesn't have to reopen the dropdown to find their new template.
      if (newDocId != null && tplEditReturn === "editLabel") setLTemplateId(Number(newDocId));
    }
    setView(tplEditReturn);
  };

  // ───────────────────────────────────────────────────────── LIST
  if (view === "list") {
    return (
      <div style={s.container}>
        <h3 style={s.title}>Labels</h3>
        <div style={s.list}>
          {labels.length === 0 && <div style={s.muted}>No labels yet.</div>}
          {labels.map((label) => (
            <div key={label.id} style={s.row}>
              <LabelChip name={label.name} color={label.color} selected />
              <span style={{ flex: 1 }} />
              <button style={s.pencil} onClick={() => openLabel(label)} aria-label="Edit label">✎</button>
            </div>
          ))}
        </div>
        <button style={s.addBtn} onClick={() => openLabel(null)}>+ Add Label</button>
        <button style={s.linkBtn} onClick={() => { setTplListReturn("list"); setView("templates"); }}>Action Templates ›</button>
        <div style={s.footer}>
          <button style={s.primaryBtn} onClick={closeModal}>Done</button>
        </div>
      </div>
    );
  }

  // ───────────────────────────────────────────────────────── COLOR
  if (view === "color") {
    return (
      <div style={s.container}>
        <h3 style={s.title}>Pick a color</h3>
        <div style={s.swatchGrid}>
          {LABEL_PRESET_COLORS.map((c) => (
            <button
              key={c}
              onClick={() => { setLColor(c); setView("editLabel"); }}
              style={{ ...s.swatch, backgroundColor: c, outline: c === lColor ? "3px solid var(--text)" : "none" }}
              aria-label={c}
            />
          ))}
        </div>
        <input value={customHex} onChange={(e) => setCustomHex(e.target.value)} placeholder="#RRGGBB" style={s.input} />
        <div style={s.btnRow}>
          <button style={s.secondaryBtn} onClick={() => setView("editLabel")}>Cancel</button>
          <button
            style={{ ...s.primaryBtn, ...(HEX_RE.test(customHex) ? {} : s.disabled) }}
            disabled={!HEX_RE.test(customHex)}
            onClick={() => { setLColor(customHex); setView("editLabel"); }}
          >Apply</button>
        </div>
      </div>
    );
  }

  // ───────────────────────────────────────────────────────── EDIT LABEL
  if (view === "editLabel") {
    return (
      <div style={s.container}>
        <h3 style={s.title}>{editingLabel ? "Edit label" : "New label"}</h3>
        <label style={s.fieldLabel}>Name</label>
        <input value={lName} onChange={(e) => { setLName(e.target.value); setError(""); }} placeholder="Label name" style={s.input} autoFocus />
        <label style={s.fieldLabel}>Color</label>
        <button style={s.colorRow} onClick={() => setView("color")}>
          <span style={{ ...s.colorDot, backgroundColor: lColor }} />
          <span style={s.colorHex}>{lColor}</span>
        </button>
        <Toggle
          title="Make global"
          desc="Visible in every project. Off keeps it in a chosen project only."
          checked={lGlobal}
          onChange={() => setLGlobal((g) => !g)}
        />
        {!lGlobal && (
          <>
            <label style={s.fieldLabel}>Project</label>
            <FloatingSelect
              value={lProjectId == null ? "" : String(lProjectId)}
              placeholder="Select a project"
              options={projects.map((p) => ({ id: String(p.id), name: p.projectName || "Untitled" }))}
              emptyText="No projects"
              onChange={(id) => setLProjectId(Number(id))}
            />
          </>
        )}
        <label style={s.fieldLabel}>Default action template</label>
        <FloatingSelect
          value={lTemplateId == null ? "__none__" : String(lTemplateId)}
          placeholder="None"
          options={[
            { id: "__none__", name: "None" },
            ...actionTemplates.map((t) => ({ id: String(t.id), name: t.name })),
          ]}
          emptyText="No templates yet"
          onChange={(id) => setLTemplateId(id === "__none__" ? null : Number(id))}
        />
        {/* Create or manage templates without leaving the label editor. A new
            template made here is auto-selected as this label's default. */}
        <div style={s.tplActionsRow}>
          <button
            style={s.tplActionBtn}
            onClick={() => openTemplate(null, "editLabel")}
          >+ New template</button>
          <button
            style={s.tplActionBtn}
            onClick={() => { setTplListReturn("editLabel"); setView("templates"); }}
          >Edit templates</button>
        </div>
        {error && <div style={s.error}>{error}</div>}
        <div style={s.btnRow}>
          {editingLabel && (
            <button style={s.deleteBtn} onClick={async () => { await deleteLabel(editingLabel.id); setView("list"); }}>Delete</button>
          )}
          <span style={{ flex: 1 }} />
          <button style={s.secondaryBtn} onClick={() => setView("list")}>Cancel</button>
          <button style={{ ...s.primaryBtn, ...(lName.trim() ? {} : s.disabled) }} disabled={!lName.trim()} onClick={saveLabel}>Save</button>
        </div>
      </div>
    );
  }

  // ───────────────────────────────────────────────────────── TEMPLATES LIST
  if (view === "templates") {
    return (
      <div style={s.container}>
        <h3 style={s.title}>Action Templates</h3>
        <div style={s.list}>
          {actionTemplates.length === 0 && <div style={s.muted}>No templates yet.</div>}
          {actionTemplates.map((t) => (
            <div key={t.id} style={s.tplRow} onClick={() => openTemplate(t, "templates")}>
              <div>
                <div style={s.tplName}>{t.name}</div>
                <div style={s.muted}>{(t.actionLabels || []).length} steps</div>
              </div>
              <span style={{ flex: 1 }} />
              <span style={s.pencil}>✎</span>
            </div>
          ))}
        </div>
        <button style={s.addBtn} onClick={() => openTemplate(null, "templates")}>+ New template</button>
        <div style={s.footer}>
          <button style={s.secondaryBtn} onClick={() => setView(tplListReturn)}>‹ Back</button>
        </div>
      </div>
    );
  }

  // ───────────────────────────────────────────────────────── EDIT TEMPLATE
  return (
    <div style={s.container}>
      <h3 style={s.title}>{editingTemplate ? "Edit template" : "Create template"}</h3>
      <label style={s.fieldLabel}>Name</label>
      <input value={tName} onChange={(e) => setTName(e.target.value)} placeholder="Template name" style={s.input} autoFocus />
      <label style={s.fieldLabel}>Steps</label>
      <div style={s.stepsList}>
        {tSteps.map((step, i) => (
          <div key={i} style={s.stepRow}>
            <input
              value={step}
              onChange={(e) => setTSteps((prev) => prev.map((v, j) => (j === i ? e.target.value : v)))}
              placeholder={`Step ${i + 1}`}
              style={{ ...s.input, flex: 1, marginBottom: 0 }}
            />
            <button style={s.stepDel} onClick={() => setTSteps((prev) => prev.filter((_, j) => j !== i))} aria-label="Remove step">✕</button>
          </div>
        ))}
      </div>
      <button style={s.addBtn} onClick={() => setTSteps((prev) => [...prev, ""])}>+ Add step</button>
      <Toggle
        title="Make global"
        desc="Visible in every project. Off keeps it in this project only."
        checked={tGlobal}
        onChange={() => setTGlobal((g) => !g)}
      />
      <div style={s.btnRow}>
        {editingTemplate && (
          <button style={s.deleteBtn} onClick={async () => { await deleteActionTemplate(editingTemplate.id); setView(tplEditReturn); }}>Delete</button>
        )}
        <span style={{ flex: 1 }} />
        <button style={s.secondaryBtn} onClick={() => setView(tplEditReturn)}>Cancel</button>
        <button style={{ ...s.primaryBtn, ...(tName.trim() ? {} : s.disabled) }} disabled={!tName.trim()} onClick={saveTemplate}>Save</button>
      </div>
    </div>
  );
};

// A dropdown whose menu is rendered in a portal (fixed, anchored to the button)
// so opening it floats over the dialog instead of growing it, and it can't be
// clipped by the modal's scroll area. Shared by the project + template pickers.
function FloatingSelect({ value, placeholder, options, emptyText = "None", onChange }) {
  const btnRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [rect, setRect] = useState(null);
  const toggle = () => {
    if (!open && btnRef.current) setRect(btnRef.current.getBoundingClientRect());
    setOpen((o) => !o);
  };
  const selected = options.find((o) => String(o.id) === String(value));
  return (
    <div style={s.ddWrap}>
      <button ref={btnRef} style={s.ddButton} onClick={toggle}>
        <span style={s.ddText}>{selected ? selected.name : placeholder}</span>
        <img src={open ? caretUp : caretDown} alt="" style={s.caret} />
      </button>
      {open && rect && createPortal(
        <>
          {/* Backdrop closes the menu on outside click; both sit above the
              modal (z 3000) so the menu floats over the dialog content. */}
          <div style={s.ddBackdrop} onClick={() => setOpen(false)} />
          <div style={{ ...s.ddMenuFloat, top: rect.bottom + 6, left: rect.left, width: rect.width }}>
            {options.map((o) => (
              <div
                key={String(o.id)}
                style={s.ddItem}
                onClick={() => { onChange(o.id); setOpen(false); }}
              >
                {o.name}
              </div>
            ))}
            {options.length === 0 && (
              <div style={{ ...s.ddItem, color: "var(--text-muted)", cursor: "default" }}>{emptyText}</div>
            )}
          </div>
        </>,
        document.body
      )}
    </div>
  );
}

function Toggle({ title, desc, checked, onChange }) {
  return (
    <div style={s.toggleRow}>
      <div>
        <div style={s.toggleTitle}>{title}</div>
        <div style={s.toggleDesc}>{desc}</div>
      </div>
      <ToggleSwitch checked={checked} onChange={() => onChange()} />
    </div>
  );
}

const s = {
  container: { display: "flex", flexDirection: "column", width: "100%", minWidth: "240px" },
  title: { margin: "0 0 12px", fontSize: "18px", color: "var(--modal-text)" },
  list: { display: "flex", flexDirection: "column", gap: "8px", maxHeight: "300px", overflowY: "auto", marginBottom: "8px" },
  stepsList: { display: "flex", flexDirection: "column", gap: "8px", marginBottom: "8px" },
  row: { display: "flex", alignItems: "center", gap: "8px" },
  tplRow: { display: "flex", alignItems: "center", gap: "8px", padding: "8px 10px", background: "var(--surface)", borderRadius: "8px", cursor: "pointer" },
  tplName: { color: "var(--text)", fontSize: "14px", fontWeight: 600 },
  muted: { color: "var(--text-muted)", fontSize: "12px" },
  pencil: { background: "none", border: "none", color: "var(--text-muted)", cursor: "pointer", fontSize: "16px", padding: "4px" },
  addBtn: { background: "var(--surface)", border: "1px dashed var(--border)", color: "var(--text)", borderRadius: "8px", padding: "10px", cursor: "pointer", marginBottom: "8px" },
  linkBtn: { background: "none", border: "none", color: "var(--primary)", cursor: "pointer", textAlign: "left", padding: "6px 0", fontSize: "14px" },
  footer: { display: "flex", justifyContent: "flex-end", marginTop: "8px" },
  fieldLabel: { fontSize: "12px", color: "var(--text-muted)", margin: "8px 0 4px" },
  input: { padding: "10px", fontSize: "15px", border: "1px solid var(--border)", background: "var(--surface)", color: "var(--text)", borderRadius: "8px", outline: "none", marginBottom: "8px", boxSizing: "border-box" },
  colorRow: { display: "flex", alignItems: "center", gap: "10px", padding: "8px 10px", border: "1px solid var(--border)", background: "var(--surface)", borderRadius: "8px", cursor: "pointer", marginBottom: "8px" },
  // Project-chip-style dropdown for the default action template.
  ddWrap: { position: "relative", marginBottom: "8px" },
  ddButton: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: "8px", width: "100%", height: "40px", padding: "0 12px", background: "var(--surface)", border: "1px solid var(--border)", borderRadius: "12px", boxShadow: "0 1px 4px rgba(0,0,0,0.25)", cursor: "pointer", boxSizing: "border-box" },
  ddText: { fontSize: "14px", fontWeight: 500, color: "var(--text)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  caret: { width: "16px", height: "16px", filter: "var(--icon-filter)", flexShrink: 0 },
  // Floating menu rendered in a portal (fixed) so it overlays the dialog
  // content instead of growing it, and can't be clipped by the modal overflow.
  ddBackdrop: { position: "fixed", inset: 0, zIndex: 4000 },
  ddMenuFloat: { position: "fixed", background: "var(--surface)", border: "1px solid var(--border)", borderRadius: "8px", maxHeight: "240px", overflowY: "auto", padding: "4px", zIndex: 4001, boxShadow: "0 8px 24px rgba(0,0,0,0.4)", boxSizing: "border-box" },
  ddItem: { padding: "8px 10px", fontSize: "14px", color: "var(--text)", cursor: "pointer", borderRadius: "6px" },
  tplActionsRow: { display: "flex", gap: "8px", marginBottom: "8px" },
  tplActionBtn: { flex: 1, background: "none", border: "1px solid var(--border)", color: "var(--primary)", borderRadius: "8px", padding: "8px", cursor: "pointer", fontSize: "13px", fontWeight: 600 },
  colorDot: { width: "24px", height: "24px", borderRadius: "50%" },
  colorHex: { color: "var(--text)", fontSize: "14px" },
  swatchGrid: { display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "12px", marginBottom: "16px" },
  swatch: { width: "40px", height: "40px", borderRadius: "50%", border: "none", cursor: "pointer", justifySelf: "center" },
  stepRow: { display: "flex", alignItems: "center", gap: "8px" },
  stepDel: { background: "none", border: "none", color: "var(--text-muted)", cursor: "pointer", fontSize: "14px" },
  toggleRow: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: "12px", margin: "8px 0" },
  toggleTitle: { color: "var(--text)", fontSize: "14px", fontWeight: "bold" },
  toggleDesc: { color: "var(--text-muted)", fontSize: "12px", marginTop: "2px" },
  btnRow: { display: "flex", alignItems: "center", gap: "10px", marginTop: "4px" },
  primaryBtn: { background: "var(--primary)", color: "#fff", border: "none", borderRadius: "8px", padding: "8px 16px", fontWeight: "bold", cursor: "pointer" },
  secondaryBtn: { background: "none", border: "none", color: "var(--text-muted)", fontWeight: "bold", cursor: "pointer", padding: "8px 4px" },
  deleteBtn: { background: "none", border: "none", color: "#ff5a5a", fontWeight: "bold", cursor: "pointer", padding: "8px 4px" },
  disabled: { backgroundColor: "var(--border)", color: "var(--text-faint)", cursor: "not-allowed" },
  error: { color: "#ff5a5a", fontSize: "13px", margin: "4px 0" },
  switch: { position: "relative", width: "46px", height: "26px", borderRadius: "13px", border: "none", cursor: "pointer", flexShrink: 0, padding: "2px" },
  knob: { display: "block", width: "22px", height: "22px", borderRadius: "50%", backgroundColor: "#fff", transition: "transform 0.15s" },
};

export default ManageLabelsDialog;
